import React from "react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import { STATUS_LABELS, STATUS_ORDER } from "./AdminDashboard.constants";
import { Flame, Clock, CircleDot, ChevronRight, Phone, Tag } from "lucide-react";

// Precomputed: status → other 3 statuses for the quick-move dropdown.
const SIBLING_STATUSES = Object.freeze(Object.fromEntries(
  STATUS_ORDER.map((s) => [s, STATUS_ORDER.filter((o) => o !== s).slice(0, 3)]),
));

// Stage palette tuned for a dark theme: a vivid accent + a subtle surface tint.
const STAGE_THEME = {
  new:            { accent: "#3B82F6", bg: "rgba(59,130,246,0.10)",  ring: "rgba(59,130,246,0.35)" },
  researched:     { accent: "#F59E0B", bg: "rgba(245,158,11,0.10)",  ring: "rgba(245,158,11,0.35)" },
  emailed:        { accent: "#0EA5E9", bg: "rgba(14,165,233,0.10)",  ring: "rgba(14,165,233,0.35)" },
  site_generated: { accent: "#FF4F00", bg: "rgba(255,79,0,0.12)",    ring: "rgba(255,79,0,0.40)"  },
  paid:           { accent: "#10B981", bg: "rgba(16,185,129,0.10)",  ring: "rgba(16,185,129,0.35)" },
  won:            { accent: "#059669", bg: "rgba(5,150,105,0.10)",   ring: "rgba(5,150,105,0.35)"  },
  lost:           { accent: "#EF4444", bg: "rgba(239,68,68,0.10)",   ring: "rgba(239,68,68,0.35)"  },
};

// ─── Drag & drop hook ────────────────────────────────────────────────────────
function useKanbanDrag(onRefresh) {
  const [movingId, setMovingId] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(null);

  const moveCard = async (bizId, newStatus) => {
    if (!bizId || !newStatus) return;
    setMovingId(bizId);
    try {
      const res = await authFetch(`${API}/businesses/${bizId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res?.ok) throw new Error();
      toast.success(`Movido a "${STATUS_LABELS[newStatus]}"`);
      onRefresh();
    } catch { toast.error("Error al mover tarjeta"); }
    finally { setMovingId(null); }
  };

  const handleDragStart = (e, bizId, fromStatus) => {
    e.dataTransfer.setData("cardId", bizId);
    e.dataTransfer.setData("fromStatus", fromStatus);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(status);
  };

  const handleDrop = (e, toStatus) => {
    e.preventDefault();
    setDragOver(null);
    const cardId = e.dataTransfer.getData("cardId");
    const fromStatus = e.dataTransfer.getData("fromStatus");
    if (cardId && fromStatus !== toStatus) moveCard(cardId, toStatus);
  };

  return { movingId, dragOver, setDragOver, moveCard, handleDragStart, handleDragOver, handleDrop };
}

// ─── Badge system ────────────────────────────────────────────────────────────
// Computes up to 2 badges per business based on activity signals.
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS  = 24 * HOUR_MS;

function computeBadges(biz) {
  const out = [];
  const now = Date.now();
  const created = biz.created_at ? new Date(biz.created_at).getTime() : 0;
  const updated = biz.updated_at ? new Date(biz.updated_at).getTime() : created;
  const ageMs   = created ? now - created : 0;
  const idleMs  = updated ? now - updated : 0;

  // HOT — lead en etapas calientes (preview generado o pago pendiente)
  if (biz.status === "site_generated" || biz.status === "paid") {
    out.push({ key: "hot", label: "HOT LEAD", icon: Flame, fg: "#FFB703", bg: "rgba(255,183,3,0.12)", bd: "rgba(255,183,3,0.45)" });
  }
  // NEW 24H — creado en las últimas 24h (no si ya es hot)
  else if (ageMs > 0 && ageMs < DAY_MS) {
    out.push({ key: "new24", label: "24H", icon: CircleDot, fg: "#60A5FA", bg: "rgba(96,165,250,0.12)", bd: "rgba(96,165,250,0.40)" });
  }
  // IN REVIEW — contactado pero sin respuesta en >48h
  if (biz.status === "emailed" && idleMs > 2 * DAY_MS) {
    out.push({ key: "review", label: "IN REVIEW", icon: Clock, fg: "#F0ABFC", bg: "rgba(240,171,252,0.10)", bd: "rgba(240,171,252,0.38)" });
  }
  // STALE — sin actualizar en >7 días y aún no cerrado
  else if (idleMs > 7 * DAY_MS && !["won", "lost", "paid"].includes(biz.status)) {
    out.push({ key: "stale", label: "STALE", icon: Clock, fg: "#94A3B8", bg: "rgba(148,163,184,0.10)", bd: "rgba(148,163,184,0.30)" });
  }
  return out.slice(0, 2);
}

// Priority ranking for in-column sort (lower = shown first)
const BADGE_PRIORITY = { hot: 0, new24: 1, review: 2, stale: 4 };

function sortByBadges(cards) {
  return [...cards].sort((a, b) => {
    const ba = computeBadges(a);
    const bb = computeBadges(b);
    const pa = ba.length ? Math.min(...ba.map(x => BADGE_PRIORITY[x.key] ?? 3)) : 3;
    const pb = bb.length ? Math.min(...bb.map(x => BADGE_PRIORITY[x.key] ?? 3)) : 3;
    if (pa !== pb) return pa - pb;
    // Tie-breaker: newest first
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
}

function Badge({ badge }) {
  const Icon = badge.icon;
  return (
    <span
      data-testid={`badge-${badge.key}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
      style={{ color: badge.fg, background: badge.bg, border: `1px solid ${badge.bd}` }}
    >
      <Icon size={9} /> {badge.label}
    </span>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────
function KanbanCard({ biz, status, movingId, onNavigate, onMove }) {
  const theme = STAGE_THEME[status] || {};
  const moving = movingId === biz.id;
  const badges = computeBadges(biz);

  return (
    <div
      data-testid={`kanban-card-${biz.id}`}
      draggable="true"
      onDragStart={(e) => onMove.dragStart(e, biz.id, status)}
      className={`group relative rounded-xl p-3 transition-all cursor-grab active:cursor-grabbing select-none ${
        moving ? "opacity-40 scale-95" : "hover:-translate-y-[2px]"
      }`}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.02), 0 8px 20px -12px rgba(0,0,0,0.6)",
        borderLeft: `3px solid ${theme.accent}`,
      }}
      onClick={() => onNavigate(biz.id)}
    >
      {/* Top row: name + chevron */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-bold text-white leading-tight truncate flex-1" data-testid="kanban-card-name">
          {biz.name}
        </p>
        <ChevronRight size={13} className="text-gray-600 group-hover:text-gray-300 transition-colors flex-shrink-0 mt-0.5" />
      </div>

      {/* Category */}
      {biz.category && (
        <div className="flex items-center gap-1 mt-1">
          <Tag size={9} className="text-gray-600" />
          <p className="text-[10px] text-gray-500 truncate">{biz.category}</p>
        </div>
      )}

      {/* Phone */}
      {biz.phone && (
        <div className="flex items-center gap-1 mt-1">
          <Phone size={9} className="text-gray-600" />
          <p className="text-[10px] text-gray-500 truncate font-mono">{biz.phone}</p>
        </div>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {badges.map(b => <Badge key={b.key} badge={b} />)}
        </div>
      )}

      {/* Quick-move pills (appear on hover) */}
      <div className="flex flex-wrap gap-1 mt-2 opacity-60 group-hover:opacity-100 transition-opacity">
        {SIBLING_STATUSES[status].map(s => {
          const st = STAGE_THEME[s] || {};
          return (
            <button
              key={s}
              data-testid={`move-${biz.id}-${s}`}
              onClick={e => { e.stopPropagation(); onMove.moveCard(biz.id, s); }}
              className="text-[8.5px] font-bold px-1.5 py-0.5 rounded-md transition-all hover:-translate-y-[1px]"
              style={{ background: st.bg, color: st.accent, border: `1px solid ${st.ring}` }}
            >
              → {STATUS_LABELS[s]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Column ──────────────────────────────────────────────────────────────────
function KanbanColumn({ status, cards, dragOver, movingId, onNavigate, onMove }) {
  const theme = STAGE_THEME[status] || {};
  const isOver = dragOver === status;

  return (
    <div
      className="flex-shrink-0 w-[290px] snap-center"
      onDragOver={(e) => onMove.handleDragOver(e, status)}
      onDragLeave={() => onMove.setDragOver(null)}
      onDrop={(e) => onMove.handleDrop(e, status)}
    >
      {/* Column header */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 rounded-xl mb-3"
        style={{
          background: theme.bg,
          border: `1px solid ${theme.ring}`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.02)`,
        }}
      >
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: theme.accent, boxShadow: `0 0 8px ${theme.accent}` }} />
        <span className="text-[11px] font-black uppercase tracking-widest text-white">{STATUS_LABELS[status]}</span>
        <span
          className="ml-auto text-[10px] font-black px-1.5 py-0.5 rounded tabular-nums"
          style={{ color: theme.accent, background: "rgba(255,255,255,0.06)" }}
          data-testid={`kanban-count-${status}`}
        >
          {cards.length}
        </span>
      </div>

      {/* Cards list */}
      <div
        className={`flex flex-col gap-2 min-h-[80px] rounded-xl transition-all duration-150 ${
          isOver ? "ring-2 ring-[#ffc700]/60 bg-[#ffc700]/5" : ""
        }`}
        style={{ padding: isOver ? "6px" : "2px" }}
      >
        {cards.length === 0 && !isOver && (
          <div className="border border-dashed rounded-xl p-4 text-center" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <p className="text-[10px] text-gray-600">Arrastrá una tarjeta acá</p>
          </div>
        )}
        {cards.map(biz => (
          <KanbanCard
            key={biz.id}
            biz={biz}
            status={status}
            movingId={movingId}
            onNavigate={onNavigate}
            onMove={onMove}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Root view ───────────────────────────────────────────────────────────────
export function KanbanView({ businesses, onNavigate, onRefresh }) {
  const { movingId, dragOver, setDragOver, moveCard, handleDragStart, handleDragOver, handleDrop } =
    useKanbanDrag(onRefresh);

  const moveHandlers = { moveCard, dragStart: handleDragStart, handleDragOver, handleDrop, setDragOver };

  return (
    <div data-testid="kanban-view" className="overflow-x-auto px-4 pt-2 pb-8 snap-x snap-mandatory scroll-smooth hide-scrollbar">
      <div className="flex gap-3 w-max" style={{ minWidth: "100%" }}>
        {STATUS_ORDER.map(status => (
          <KanbanColumn
            key={status}
            status={status}
            cards={sortByBadges(businesses.filter(b => b.status === status))}
            dragOver={dragOver}
            movingId={movingId}
            onNavigate={onNavigate}
            onMove={moveHandlers}
          />
        ))}
      </div>
      <p className="text-center text-[10px] text-gray-600 mt-4 uppercase tracking-widest">
        Arrastrá tarjetas para moverlas entre etapas · Badges calculados automáticamente
      </p>
    </div>
  );
}
